import { EMAIL_CONFIG, getMailgunUrl } from '../config/email';

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export interface EmailResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface PropertyInquiryData {
  name: string;
  email: string;
  phone?: string;
  message: string;
  propertyTitle: string;
  propertyLink?: string;
  inquiryType: 'contact' | 'message' | 'inspection';
  agentName: string;
  agentEmail: string;
}

export interface ForgotPasswordData {
  email: string;
  resetLink: string;
  userName?: string;
}

/**
 * Send contact form email using Mailgun
 * Mailgun is a reliable email service for production applications
 */
export const sendContactEmail = async (formData: ContactFormData): Promise<EmailResponse> => {
  try {
    // Get Mailgun URL from configuration
    const MAILGUN_URL = getMailgunUrl();
    
    // Prepare email data for Mailgun
    const emailData = new FormData();
    emailData.append('from', `${formData.name} <noreply@${EMAIL_CONFIG.MAILGUN.DOMAIN}>`);
    emailData.append('to', EMAIL_CONFIG.MAILGUN.RECIPIENT);
    emailData.append('subject', `Contact Form: ${formData.subject}`);
    emailData.append('text', `
Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}

Message:
${formData.message}

---
This email was sent from the RentaBahay contact form.
    `.trim());
    emailData.append('reply-to', formData.email);
    
    // Send email via Mailgun API
    const response = await fetch(MAILGUN_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`api:${EMAIL_CONFIG.MAILGUN.API_KEY}`)}`,
      },
      body: emailData
    });

    if (response.ok) {
      return {
        success: true,
        message: 'Email sent successfully!'
      };
    } else {
      const errorData = await response.text();
      throw new Error(`Mailgun API error: ${response.status} - ${errorData}`);
    }

  } catch (error) {
    console.error('Failed to send email via Mailgun:', error);
    
    // Fallback to mailto link if Mailgun fails
    try {
      const subject = encodeURIComponent(formData.subject);
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      );
      const mailtoUrl = `mailto:${EMAIL_CONFIG.MAILGUN.RECIPIENT}?subject=${subject}&body=${body}`;
      
      window.location.href = mailtoUrl;
      
      return {
        success: true,
        message: 'Opened email client. Please send the email manually.'
      };
    } catch (fallbackError) {
      return {
        success: false,
        message: 'Failed to send email. Please try again later.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};

/**
 * Send property inquiry email using Mailgun
 * This handles Contact Agent, Send Message, and Request Inspection emails
 */
export const sendPropertyInquiryEmail = async (inquiryData: PropertyInquiryData): Promise<EmailResponse> => {
  // Validate required fields
  if (!inquiryData.agentEmail || inquiryData.agentEmail.trim() === '') {
    return {
      success: false,
      message: 'Agent email is required to send inquiry',
      error: 'Missing agent email address'
    };
  }

  // Determine subject based on inquiry type
  let subject = '';
  switch (inquiryData.inquiryType) {
    case 'contact':
      subject = `Property Inquiry: ${inquiryData.propertyTitle}`;
      break;
    case 'message':
      subject = `Message about ${inquiryData.propertyTitle}`;
      break;
    case 'inspection':
      subject = `Inspection Request: ${inquiryData.propertyTitle}`;
      break;
    default:
      subject = `Property Inquiry: ${inquiryData.propertyTitle}`;
  }
  
  // Create email body
  const emailBody = `
Inquiry from: ${inquiryData.name}
Email: ${inquiryData.email}
${inquiryData.phone ? `Phone: ${inquiryData.phone}` : ''}
Inquiry Type: ${inquiryData.inquiryType.charAt(0).toUpperCase() + inquiryData.inquiryType.slice(1)}

Property: ${inquiryData.propertyTitle}
${inquiryData.propertyLink ? `Property Link: ${inquiryData.propertyLink}` : ''}

Message:
${inquiryData.message}

---
This inquiry was sent from RentaBahay.com
  `.trim();

  try {
    // Get Mailgun URL from configuration
    const MAILGUN_URL = getMailgunUrl();
    
    // Prepare email data for Mailgun
    const emailData = new FormData();
    emailData.append('from', `${inquiryData.name} <noreply@${EMAIL_CONFIG.MAILGUN.DOMAIN}>`);
    emailData.append('to', inquiryData.agentEmail);
    emailData.append('subject', subject);
    emailData.append('text', emailBody);
    emailData.append('reply-to', inquiryData.email);
    
    // Send email via Mailgun API
    const response = await fetch(MAILGUN_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`api:${EMAIL_CONFIG.MAILGUN.API_KEY}`)}`,
      },
      body: emailData
    });

    if (response.ok) {
      return {
        success: true,
        message: 'Email sent successfully!'
      };
    } else {
      const errorData = await response.text();
      throw new Error(`Mailgun API error: ${response.status} - ${errorData}`);
    }

  } catch (error) {
    console.error('Failed to send property inquiry email via Mailgun:', error);
    
    // Fallback to mailto link if Mailgun fails
    try {
      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(emailBody);
      const mailtoUrl = `mailto:${inquiryData.agentEmail}?subject=${encodedSubject}&body=${encodedBody}`;
      
      window.location.href = mailtoUrl;
      
      return {
        success: true,
        message: 'Opened email client. Please send the email manually.'
      };
    } catch (fallbackError) {
      return {
        success: false,
        message: 'Failed to send email. Please try again later.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};

/**
 * Send forgot password email using Mailgun
 * This handles password reset emails with a reset link
 */
export const sendForgotPasswordEmail = async (data: ForgotPasswordData): Promise<EmailResponse> => {
  try {
    // Get Mailgun URL from configuration
    const MAILGUN_URL = getMailgunUrl();
    
    // Prepare email data for Mailgun
    const emailData = new FormData();
    emailData.append('from', `RentaBahay Support <noreply@${EMAIL_CONFIG.MAILGUN.DOMAIN}>`);
    emailData.append('to', data.email);
    emailData.append('subject', 'Reset Your Password - RentaBahay');
    emailData.append('text', `
Hello${data.userName ? ` ${data.userName}` : ''},

You recently requested to reset your password for your RentaBahay account. Click the link below to reset it:

${data.resetLink}

This password reset link will expire in 1 hour for security reasons.

If you did not request a password reset, please ignore this email. Your password will remain unchanged.

If you have any questions, please contact our support team.

Best regards,
RentaBahay Support Team

---
This email was sent from RentaBahay.com
    `.trim());
    
    // Send email via Mailgun API
    const response = await fetch(MAILGUN_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`api:${EMAIL_CONFIG.MAILGUN.API_KEY}`)}`,
      },
      body: emailData
    });

    if (response.ok) {
      return {
        success: true,
        message: 'Password reset email sent successfully!'
      };
    } else {
      const errorData = await response.text();
      throw new Error(`Mailgun API error: ${response.status} - ${errorData}`);
    }

  } catch (error) {
    console.error('Failed to send forgot password email via Mailgun:', error);
    
    // Fallback to mailto link if Mailgun fails
    try {
      const subject = encodeURIComponent('Reset Your Password - RentaBahay');
      const body = encodeURIComponent(`
Hello${data.userName ? ` ${data.userName}` : ''},

You recently requested to reset your password for your RentaBahay account. Click the link below to reset it:

${data.resetLink}

This password reset link will expire in 1 hour for security reasons.

If you did not request a password reset, please ignore this email. Your password will remain unchanged.

If you have any questions, please contact our support team.

Best regards,
RentaBahay Support Team
      `.trim());
      
      const mailtoUrl = `mailto:${data.email}?subject=${subject}&body=${body}`;
      
      window.location.href = mailtoUrl;
      
      return {
        success: true,
        message: 'Opened email client. Please send the email manually.'
      };
    } catch (fallbackError) {
      return {
        success: false,
        message: 'Failed to send email. Please try again later.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};

/**
 * Alternative implementation using fetch to your backend API
 * Uncomment and modify this if you have a backend API endpoint
 */
/*
export const sendContactEmailViaAPI = async (formData: ContactFormData): Promise<EmailResponse> => {
  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        message: formData.message,
        to_email: 'vloremia@gmail.com'
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    return {
      success: true,
      message: result.message || 'Email sent successfully!'
    };
  } catch (error) {
    console.error('Failed to send email via API:', error);
    return {
      success: false,
      message: 'Failed to send email. Please try again later.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};
*/
